// src/data/feedback.ts
// --------------------
// Repository for the `shelter_feedback` table — the Shelter Feedback module
// (docs/feedback-module-plan.md). Shelter workers log feedback (memo + date +
// optional attachment); admins review all of it. Typed via getTypedClient()
// against the generated Database schema.
//
// RLS does the scoping (db-23): a worker's SELECT only returns their own rows; an
// admin sees everything. listMyFeedback still filters submitted_by explicitly so
// an admin reusing it would get a single worker's history, not all of it.
//
// Functions log on error and return null (reads) / { error } (writes), matching
// the rest of src/data/. Write params are loose `object`, cast to the generated
// Insert/Update at the boundary (call-site form objects keep compiling).
//
// NOTE: the generated `shelter_feedback` types only exist AFTER db-23 is applied
// and `npm run gen:types` is re-run — do that before `npm run typecheck`.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

// The app-facing feedback row (hand-shaped; nullable where the DB allows null).
export type FeedbackRow = {
  id: number;
  title: string | null;
  memo: string;
  date_received: string | null;
  submitted_by: string | null;
  shelter_id: string;
  shelter_code: string | null;
  attachment_path: string | null;
  attachment_name: string | null;
  reviewed: boolean;
  created_at: string;
  deactivated: boolean;
};

type FeedbackInsert = Database["public"]["Tables"]["shelter_feedback"]["Insert"];
type FeedbackUpdate = Database["public"]["Tables"]["shelter_feedback"]["Update"];

// The admin-editable "thank you" reply sent to the worker when feedback is marked
// reviewed. Stored in portal_config under this key (a plain JSON string), edited in
// Admin -> System Settings, and used as the default text in the review modal.
export const FEEDBACK_REVIEW_TEMPLATE_KEY = "feedback_review_template";
export const DEFAULT_FEEDBACK_REVIEW_TEMPLATE =
  "Hi\nThank you, we appreciate your feedback which will be shared with team of volunteers.\nKind regards\nTeam Zephyr";

// Columns the app reads. Typed as `string` (not a literal) on purpose — same
// reasoning as REQUEST_COLS: a literal makes supabase-js build a deep result type.
export const FEEDBACK_COLS: string =
  "id,title,memo,date_received,submitted_by,shelter_id,shelter_code,attachment_path,attachment_name,reviewed,created_at,deactivated";

const ORDER_COL = "created_at";

// Insert a feedback row and return its new id (for the email link + UI). RLS-
// critical create: the WITH CHECK policy requires submitted_by = auth.uid() and a
// shelter the worker belongs to. Returns { id, error }; id is null on error/no client.
export async function insertFeedback(
  row: object
): Promise<{ id: number | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { id: null, error: null };
  const { data, error } = await c
    .from("shelter_feedback")
    .insert(row as FeedbackInsert)
    .select("id")
    .single();
  if (error) logger.warn("insertFeedback failed:", error.message);
  return { id: (data as { id: number } | null)?.id ?? null, error };
}

// A single worker's own feedback, newest first (active only). RLS already scopes a
// worker to their own rows; the explicit filter keeps it correct if an admin calls it.
export async function listMyFeedback(userId: string): Promise<FeedbackRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelter_feedback")
    .select(FEEDBACK_COLS)
    .eq("submitted_by", userId)
    .eq("deactivated", false)
    .order(ORDER_COL, { ascending: false });
  if (error) {
    logger.warn("listMyFeedback failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as FeedbackRow[];
}

// All active feedback, newest first (admin review — RLS allows only admins to read
// beyond their own). Shelter-attribute + date filtering is applied in the view.
export async function listAllFeedback(): Promise<FeedbackRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelter_feedback")
    .select(FEEDBACK_COLS)
    .eq("deactivated", false)
    .order(ORDER_COL, { ascending: false });
  if (error) {
    logger.warn("listAllFeedback failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as FeedbackRow[];
}

// A single feedback row by id (the /feedback/:id deep link). Null if none/error.
export async function getFeedbackById(id: number): Promise<FeedbackRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("shelter_feedback")
    .select(FEEDBACK_COLS)
    .eq("id", id)
    .maybeSingle();
  if (error) {
    logger.warn("getFeedbackById failed:", error.message);
    return null;
  }
  return (data as unknown as FeedbackRow) ?? null;
}

// Update a feedback row (admin only via RLS): Mark reviewed, or soft-delete
// (deactivated = true). Returns { error }.
export async function updateFeedback(
  id: number,
  patch: object
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("shelter_feedback")
    .update(patch as FeedbackUpdate)
    .eq("id", id);
  if (error) logger.warn("updateFeedback failed:", error.message);
  return { error };
}
